import React from "react";
import { findInvoiceCreditCard } from "../utils/credit-card-mask";

const STATES = {
    Processed: { tone: "paid", key: "payment_state_paid" },
    Partially_Paid: { tone: "partial", key: "payment_state_partial" },
    Processing: { tone: "processing", key: "payment_state_processing" },
    REQUEST_APPROVAL: { tone: "processing", key: "payment_state_awaiting_approval" },
    Denied: { tone: "denied", key: "payment_state_denied" },
};
const NOT_PAID = { tone: "none", key: "payment_state_not_paid" };

const icon = (paths, extra) => (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...extra}>
        {paths}
    </svg>
);
const TONE_ICONS = {
    paid: icon(<><circle cx="8" cy="8" r="6.2" /><path d="M5.4 8.2l1.8 1.8 3.4-3.6" /></>),
    partial: icon(<><circle cx="8" cy="8" r="6.2" /><path d="M8 4.2a3.8 3.8 0 010 7.6z" fill="currentColor" stroke="none" /></>),
    processing: icon(<><circle cx="8" cy="8" r="6.2" /><path d="M8 4.6V8l2.3 1.5" /></>),
    denied: icon(<><circle cx="8" cy="8" r="6.2" /><path d="M5.2 5.2l5.6 5.6" /></>),
    none: icon(<circle cx="8" cy="8" r="6.2" strokeDasharray="2.2 2.2" />),
};
const CHEQUE_ICON = icon(<><rect x="1.6" y="4" width="12.8" height="8" rx="1.4" /><circle cx="8" cy="8" r="1.7" /></>, { strokeWidth: 1.6 });
const CARD_ICON = icon(<><rect x="1.6" y="3.4" width="12.8" height="9.2" rx="1.6" /><path d="M1.6 6.6h12.8M4 10h2.6" /></>, { strokeWidth: 1.6 });
const CALENDAR_ICON = icon(<><rect x="2" y="3" width="12" height="11" rx="1.6" /><path d="M2 6.6h12M5.3 1.8v2.4M10.7 1.8v2.4" /></>, { strokeWidth: 1.6 });

const toAmount = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

export const formatPaymentAmount = (value, lang) => {
    const number = toAmount(value);
    if (number === null) return null;
    const digits = new Intl.NumberFormat(lang === "fr" ? "fr-CA" : "en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(number);
    // The sign goes before the $ in English: -$25.00, not $-25.00.
    return lang === "fr" ? `${digits}\u00a0$` : `${digits.startsWith("-") ? "-" : ""}$${digits.replace("-", "")}`;
};

// A payment date is a calendar day, not an instant: read its date part so a
// midnight timestamp never slides to the previous day in a western time zone.
export const formatPaymentDate = (value, lang) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ""));
    const date = match ? new Date(Date.UTC(+match[1], +match[2] - 1, +match[3])) : new Date(value);
    if (!value || Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat(lang === "fr" ? "fr-CA" : "en-CA", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" }).format(date);
};

const cardDigits = (card) => String(card?.card_masked_number ?? "").replace(/\D/g, "").slice(-4);

export default function PaymentStatusCell({ invoice, creditCards, t }) {
    const payment = invoice?.payment;
    // Language is read on every render; nothing is memoized across an en/fr switch.
    const lang = t.lang;
    const isCard = invoice?.type === "p-card";
    const card = isCard ? findInvoiceCreditCard(creditCards, invoice) : undefined;
    // A card receipt was paid by card when it was made, so it has no payment
    // record to wait for: the card line is shown only when its card is known.
    const status = payment?.status || (isCard ? "Processed" : undefined);
    const state = status ? STATES[status] : undefined;
    // A status we do not know is shown as stored rather than called "not paid".
    const unknownStatus = status && !state ? status : null;
    const { tone, key } = state || NOT_PAID;

    const invoiceTotal = toAmount(invoice?.json?.TotalWithTax);
    const paid = isCard ? invoiceTotal : toAmount(payment?.PaymentAmount);
    // The partial breakdown needs 3E's open amount: it is exact, and paid + open
    // is the total, so the three figures add up. Sage stores no open amount and
    // its PaymentAmount is only the last instalment, so a Sage partial payment
    // shows the pill alone rather than a bar and a remaining that would be wrong.
    const openAmount = toAmount(payment?.open_amount);
    const total = openAmount !== null && paid !== null ? paid + openAmount : null;
    // A credit is applied in negative amounts (Juris): paid and total share its sign.
    const isPartial = status === "Partially_Paid" && total !== null && total !== 0;
    const remaining = isPartial ? openAmount : null;
    // Without the open amount a partial payment's PaymentAmount may be only the
    // last instalment: show no figure the cell cannot back.
    const unbackedPartial = status === "Partially_Paid" && !isPartial;
    const percentPaid = isPartial ? Math.min(1, Math.max(0, paid / total)) : null;
    const date = formatPaymentDate(payment?.CheckDate, lang);
    const digits = cardDigits(card);
    const chequeNumber = !isCard && payment?.CheckNumber != null && payment.CheckNumber !== "" ? payment.CheckNumber : null;
    const hasPayment = !!status;

    return (
        <div className="payment-cell" data-tone={tone}>
            <div className="payment-cell__top">
                <span className={`payment-pill payment-pill--${tone}`}>
                    {TONE_ICONS[tone]}
                    {unknownStatus || t.read(`invoice.${key}`)}
                </span>
                {hasPayment && paid !== null && !unbackedPartial && <span className="payment-cell__amount">{formatPaymentAmount(paid, lang)}</span>}
            </div>
            {isPartial && (
                <>
                    <div className="payment-cell__bar" role="img" aria-label={`${t.read("invoice.payment_state_partial")} ${new Intl.NumberFormat(lang === "fr" ? "fr-CA" : "en-CA", { style: "percent", maximumFractionDigits: 0 }).format(percentPaid)}`}>
                        <i style={{ width: `${percentPaid * 100}%` }} />
                    </div>
                    <div className="payment-cell__facts">
                        <span className="payment-cell__fact">{t.read("invoice.payment_of_total")} <b>{formatPaymentAmount(total, lang)}</b></span>
                        <span className="payment-cell__fact">{t.read("invoice.payment_remaining")} <b>{formatPaymentAmount(remaining, lang)}</b></span>
                    </div>
                </>
            )}
            {hasPayment && !isPartial && (chequeNumber !== null || digits || date) && (
                <div className="payment-cell__facts payment-cell__facts--stacked">
                    {chequeNumber !== null && <span className="payment-cell__fact">{CHEQUE_ICON}{t.read("invoice.payment_cheque_label")} <b>{chequeNumber}</b></span>}
                    {digits && <span className="payment-cell__fact">{CARD_ICON}{t.read("invoice.payment_card_label")} <b>••{digits}</b></span>}
                    {date && <span className="payment-cell__fact">{CALENDAR_ICON}{date}</span>}
                </div>
            )}
        </div>
    );
}
